<template>
    <div class="contact-distributions-by-ad" :class="{ fullscreen: fullScreen }">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>

        <div class="chart-wrapper">
            <DoubleBarChart
                :title="title"
                :xAxisData="getXAxisData()"
                :yAxisData="getYAxisData()"
                :series="getSeries()"
                v-if="!tableMode"
            />
            <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        </div>

        <div class="stats">
            <StatCard :title="$t('En Etkili Reklam')" :value="getStats().maxAd" />
            <StatCard :title="$t('En Az Etkili Reklam')" :value="getStats().minAd" />
            <StatCard :title="$t('Toplam Kişi Sayısı')" :value="formatStat(getStats().total).value" unit="Kişi" />
        </div>
    </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import DoubleBarChart from "../../Pages/Chatbot/pages/reports/charts/DoubleBarChart.vue";
import { expertToExcel } from "../../../utils/utils";
import fscreen from "fscreen";

const sourceTypeNames = {
    post: "Gönderi",
    ad: "Reklam",
    ADS: "Reklam",
};

export default {
    name: "ContactDistributionsByAd",
    components: {
        IconButton,
        StatCard,
        TableView,
        DoubleBarChart,
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            tableMode: false,
            fullScreen: false,
            cancels: [],
            loadingInstance: null,
        };
    },
    methods: {
        formatStat(value) {
            if (isNaN(value) || value === null || value === undefined) {
                return {
                    value: this.$t("report.noEnoughtData"),
                    unit: "",
                };
            }
            return {
                value: value,
                unit: this.$t("report.contact"),
            };
        },
        getStats() {
            if (!this.data || this.data.length === 0) {
                return {
                    maxAd: this.$t("report.noEnoughtData"),
                    minAd: this.$t("report.noEnoughtData"),
                    total: 0,
                };
            }

            const sortedData = _.orderBy(this.data, ["count"], ["desc"]);
            return {
                maxAd: sortedData[0].headline || "N/A",
                minAd: sortedData[sortedData.length - 1].headline || "N/A",
                total: _.sumBy(sortedData, "count"),
            };
        },
        getCols() {
            return [
                {
                    title: this.$t("report.adTitle"),
                    dataIndex: "headline",
                    key: "headline",
                },
                {
                    title: this.$t("report.sourceType"),
                    dataIndex: "sourceType",
                    key: "sourceType",
                },
                {
                    title: this.$t("report.contactCount"),
                    dataIndex: "count",
                    key: "count",
                },
            ];
        },
        getRows() {
            return (this.data || []).map((item) => ({
                headline: item.headline || "N/A",
                sourceType: sourceTypeNames[item.sourceType] || item.sourceType,
                count: item.count,
            }));
        },
        getXAxisData() {
            const items = this.data || [];
            const max = _.max(items.map((x) => x.count)) || 0;

            return {
                type: "value",
                max: max * 1.1,
                minInterval: 1,
                splitNumber: 10,
                splitLine: {
                    show: false,
                },
                axisLabel: {
                    rotate: -30,
                },
            };
        },
        getYAxisData() {
            const items = this.data || [];
            return {
                type: "category",
                data: items.map((item) => item.headline || "N/A"),
                axisLabel: {
                    interval: 0,
                    rotate: 30,
                },
                splitLine: {
                    show: false,
                },
            };
        },
        getSeries() {
            const items = this.data || [];
            return [
                {
                    colorBy: "data",
                    showBackground: true,
                    type: "bar",
                    label: {
                        position: "right",
                        show: true,
                    },
                    data: items.map((item) => item.count),
                },
            ];
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".contact-distributions-by-ad");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        exportToExcel() {
            const excelData = this.getRows();
            expertToExcel(excelData, `ad_distributions_${moment().format("YYYY-MM-DD")}.xlsx`, "Sheet 1");
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".contact-distributions-by-ad"),
            name: "wave",
        });
        if (this.loading) {
            this.loadingInstance.show();
        }
    },
    created() {
        const fullscreenCallback = () => {
            this.fullScreen = fscreen.fullscreenElement !== null;
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
};
</script>

<style lang="scss">
.contact-distributions-by-ad {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .chart-wrapper {
            height: calc(100vh - 200px);
        }
    }

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }
    }

    .chart-wrapper {
        min-height: 300px;
        height: auto;
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
